import FormPage from '@/components/FormPage'
import { DateTime } from 'luxon'
import {
  formatAddress,
  formatAddressForeigner,
  formatPhoneNumber,
  getNumeralFromRm,
  getRm,
  getStateFromMyKad,
  spIcon,
  newIcon,
  mnpIcon,
  mnpClass,
  stringGen,
} from '@/utils'
import {
  createGetParams,
  createPostParams,
} from '@/rest'

const formTitle = 'Register Black Subscriber'

export default {
  extends: FormPage,
  props: ['id'],
  data () {
    return {
      agree: false,
      notification: false,
      billingAddress: {
        street1: {
          component: () => import(/* webpackChunkName: "textField" */ '@/components/TextField'),
          name: 'Street',
          props: {
            label: 'Enter street',
            required: true,
            rules: [val => !!val || 'Street required!'],
            validateOnBlur: true,
          },
        },
        street2: {
          component: () => import(/* webpackChunkName: "textField" */ '@/components/TextField'),
          name: '',
          props: {
            label: 'Enter line 2 of street',
            rules: [],
          },
        },
        city: {
          component: () => import(/* webpackChunkName: "textField" */ '@/components/TextField'),
          name: 'City',
          props: {
            label: 'Enter city',
            required: true,
            rules: [val => !!val || 'City required!'],
          },
        },
        postalCode: {
          component: () => import(/* webpackChunkName: "textField" */ '@/components/TextField'),
          name: 'Postal code',
          props: {
            label: 'Enter postal code',
            mask: '#####',
            maxlength: 5,
            required: true,
            rules: [
              val => !!val || 'Postal code required!',
              val => (val && val.length === 5) || 'Invalid postal code!',
            ],
            validateOnBlur: true,
          },
        },
        state: {
          component: () => import(/* webpackChunkName: "stateProvinceField" */ '@/components/StateProvinceField'),
          props: {
            required: true,
            rules: [val => !!val || 'State required!'],
          },
          name: 'State/Province',
        },
      },
      breadcrumbs: [
        {
          text: 'Dealer Kits', disabled: false, to: '/dealer_kits',
        },
        {
          text: 'Black', disabled: false, to: '/black_kits',
        },
        {
          text: formTitle, disabled: true,
        },
      ],
      debounce: 1500,
      searchTimeout: null,
      step: 1,
      title: formTitle,
      // Form steps
      registrationDetails: {
        fields: {
          mnpStatusInfo: {
            component: () => import(/* webpackChunkName: "mnpStatusInfo" */ '@/components/MnpStatusInfo'),
            name: 'Mnp Status',
            info: null,
          },
          erecharge: {
            name: 'E-recharge balance',
          },
          ugu: {
            component: () => import(/* webpackChunkName: "msisdnField" */ '@/components/MsisdnField'),
            name: 'User-Gets-User',
            props: {
              existing: true,
              hint: 'Mobile number of the referrer',
              label: 'Enter UGU',
              noSelf: true,
              noDealer: true,
              ugu: true,
            },
          },
          msisdnList: {
            name: 'Msisdn list',
            component: () => import(/* webpackChunkName: "msisdnListField" */ '@/components/MsisdnListField'),
            props: {
              mnpResubmit: false,
              items: [],
            },
          },
          requiredEcash: {
            name: 'Required e-recharge',
            strong: true,
          },
        },
        subTitle: 'Mobile number selections',
        title: 'Registration Details',
      },
      profile: {
        fields: {
          uploadMyKad: {
            name: 'File upload',
            component: () => import(/* webpackChunkName: "nricImageUploadField" */ '@/components/NricImageUploadField'),
          },
          /*
          ocr: {
            name: 'Ocr',
            component: () => import('@/components/OcrField'),
            props: {
              step: 1
            }
          },
          */
          title: {
            component: () => import(/* webpackChunkName: "titleField" */ '@/components/TitleField'),
            name: 'Title',
            props: {
              rules: [val => !!val || 'Title required!'],
            },
          },
          name: {
            component: () => import(/* webpackChunkName: "textField" */ '@/components/TextField'),
            name: 'Full name',
            props: {
              hint: 'Exactly as in Id card',
              label: 'Enter full name',
              persistentHint: true,
              required: true,
              rules: [
                val => !!val || 'Full name required!',
              ],
            },
          },
          id: {
            component: () => import(/* webpackChunkName: "personalIdField" */ '@/components/PersonalIdField'),
            name: 'MyKad',
            props: {
              label: 'Enter MyKad number',
              // hint: 'MyKad/Passport/Armed Force number',
              rules: [
                val => !!val || 'MyKad number required!',
                val => (val && val.length === 12) || 'Invalid MyKad',
              ],
            },
          },
          dob: {
            component: () => import(/* webpackChunkName: "dobField" */ '@/components/DobField'),
            name: 'Date of birth',
            props: {
              required: true,
            },
          },
          gender: {
            component: () => import(/* webpackChunkName: "genderField" */ '@/components/GenderField'),
            name: 'Gender',
          },
          race: {
            component: () => import(/* webpackChunkName: "raceField" */ '@/components/RaceField'),
            name: 'Race',
          },
          maidenName: {
            component: () => import(/* webpackChunkName: "textField" */ '@/components/TextField'),
            hide: false,
            name: 'Mother maiden name',
            props: {
              hint: 'Name before she got married',
              label: 'Enter mother maiden name',
              persistentHint: true,
            },
          },
          fcountry: {
            name: 'Country',
            hide: true,
          },
          fstatus: {
            component: () => import(/* webpackChunkName: "statusField" */ '@/components/StatusField'),
            name: 'Foreigner status',
            hide: true,
            props: {
              list: [
                { text: 'Tourist', value: 'T' },
                { text: 'Student', value: 'S' },
                { text: 'Worker', value: 'W' }],
              itemValue: 'value',
              itemText: 'text',
              label: 'Select status foreigner',
              returnObject: true,
              persistentHint: true,
              required: true,
            },
          },
        },
        subTitle: 'Subscriber identity',
        title: 'Profile',
      },
      contactDetails: {
        fields: {
          contactNumber: {
            component: () => import(/* webpackChunkName: "textField" */ '@/components/TextField'),
            name: 'Contact number',
            props: {
              hint: 'Contactable land or mobile number',
              label: 'Enter phone number',
              required: true,
              rules: [
                val => !!val || 'Contact number required!',
              ],
            },
          },
          billDelivery: {
            component: () => import(/* webpackChunkName: "billDeliveryField" */ '@/components/BillDeliveryField'),
            name: 'Bill delivery',
          },
          email: {
            // component: () => import(/* webpackChunkName: "emailField" */ '@/components/EmailField'),
            component: () => import(/* webpackChunkName: "unrestrictedEmailField" */ '@/components/UnrestrictedEmailField'),
            name: 'Email',
            props: {
              clearable: true,
              label: 'Enter email address',
              rules: [],
              validateOnBlur: true,
              required: true,
            },
            validate: () => {},
          },
          street1: {
            component: () => import(/* webpackChunkName: "textField" */ '@/components/TextField'),
            name: 'Street',
            props: {
              label: 'Enter street',
              required: true,
              rules: [val => !!val || 'Street required!'],
              validateOnBlur: true,
            },
            section: 'Address on MyKad',
          },
          street2: {
            component: () => import(/* webpackChunkName: "textField" */ '@/components/TextField'),
            name: '',
            props: {
              label: 'Enter line 2 of street',
            },
          },
          city: {
            component: () => import(/* webpackChunkName: "textField" */ '@/components/TextField'),
            name: 'City',
            props: {
              label: 'Enter city',
              required: true,
              rules: [val => !!val || 'City required!'],
            },
          },
          postalCode: {
            component: () => import(/* webpackChunkName: "textField" */ '@/components/TextField'),
            name: 'Postal code',
            props: {
              label: 'Enter postal code',
              mask: '#####',
              maxlength: 5,
              required: true,
              rules: [
                val => !!val || 'Postal code required!',
                val => (val && val.length === 5) || 'Invalid postal code!',
              ],
              validateOnBlur: true,
            },
          },
          state: {
            component: () => import(/* webpackChunkName: "stateProvinceField" */ '@/components/StateProvinceField'),
            name: 'State',
            props: {
              required: true,
              rules: [val => !!val || 'State required!'],
            },
          },
          billingAddress: {
            component: () => import(/* webpackChunkName: "addressField" */ '@/components/AddressField'),
            fields: {},
            name: 'Billing address',
          },
          fstreet1: {
            name: 'Street',
            hide: true,
          },
          fstreet2: {
            name: 'Street2',
            hide: true,
          },
          fcity: {
            name: 'City',
            hide: true,
          },
          fpostalCode: {
            name: 'Postal code',
            hide: true,
          },
          fstate: {
            name: 'State',
            hide: true,
          },
        },
        subTitle: 'Contact & billing information',
        title: 'Contact details',
      },
      submission: {
        fields: {
          ugu: {
            name: 'UGU',
          },
          title: {
            name: 'Title',
          },
          name: {
            name: 'Name',
          },
          id: {
            name: 'MyKad',
          },
          dob: {
            name: 'Date of birth',
          },
          gender: {
            name: 'Gender',
          },
          race: {
            name: 'Race',
          },
          maidenName: {
            name: 'Mother maiden name',
          },
          contactNumber: {
            name: 'Contact number',
          },
          billDelivery: {
            name: 'Bill delivery',
          },
          email: {
            name: 'Email',
          },
          address: {
            name: 'Address',
          },
          billingAddress: {
            name: 'Billing address',
          },
          fcountry: {
            name: 'Issue country',
            hide: true,
          },
          fstatus: {
            name: 'Status',
            hide: true,
          },
          foreignerAddress: {
            name: 'Foreigner address',
            hide: true,
          },
          msisdnSummary: {
            name: 'Msisdn list',
            component: () => import(/* webpackChunkName: "msisdnSummaryField" */ '@/components/MsisdnSummaryField'),
            props: {
              items: [],
            },
          },
          chargeable: {
            emphasize: true,
            name: 'Chargeable amount',
          },
        },
        submit: false,
        subTitle: 'Check before submit',
        title: 'Submit',
      },
    }
  },
  computed: {
    chargeableAmount: function () {
      let chargeableAmount = 0
      this.registrationDetails.fields.msisdnList.props.items.forEach(item => {
        // chargeableAmount += item.chargeable
        chargeableAmount += Number(item.plan.price)
      })
      return chargeableAmount
    },
    erecharge: function () {
      return this.registrationDetails.fields.erecharge
    },
    // Override
    formSteps: function () {
      return {
        registrationDetails: this.registrationDetails,
        profile: this.profile,
        contactDetails: this.contactDetails,
        submission: this.submission,
      }
    },
    registrationItems: function () {
      return this.registrationDetails.fields.msisdnList.props.items
    },
    requiredEcash: function () {
      let requiredEcash = 0.00

      this.registrationDetails.fields.msisdnList.props.items.forEach(item => {
        const price = Number(item.plan.price)

        if (item.plan.fixedPrice) {
          requiredEcash += (price - 10)
        } else {
          requiredEcash += (price - 10) / 2
        }
      })

      return requiredEcash
    },
    address () {
      const address = formatAddress(this.contactDetails.fields)
      if (!this.contactDetails.fields.billingAddress.value) {
        this.submission.fields.billingAddress.value = address
      }
      return address
    },
    billToAddress () {
      return formatAddress(this.contactDetails.fields.billingAddress.fields)
    },
    foreignerAddress () {
      const foreignerAddress = formatAddressForeigner(this.contactDetails.fields)
      return foreignerAddress
    },
    foreignerStatus: function () {
      return this.profile.fields.fstatus.value
    },
  },
  watch: {
    agree: function (val) {
      val && this.doSubmit()
    },
    step: function (val) {
      this.profile.fields.ocr.props.step = val
    },
    'registrationDetails.fields.ugu.value': function (val) {
      this.submission.fields.ugu.value = formatPhoneNumber(val)
    },
    'profile.fields.title.value': function (val) {
      this.submission.fields.title.value = val
    },
    'profile.fields.name.value': function (val) {
      this.submission.fields.name.value = val
    },
    'profile.fields.dob.value': function (val) {
      this.submission.fields.dob.value = val
    },
    'profile.fields.maidenName.value': function (val) {
      this.submission.fields.maidenName.value = val
    },
    'contactDetails.fields.contactNumber.value': function (val) {
      this.submission.fields.contactNumber.value = val
    },
    'contactDetails.fields.email.value': function (val) {
      this.submission.fields.email.value = val
    },
    'contactDetails.fields.billDelivery.value': function (val) {
      if (val === 'email') {
        this.submission.fields.billDelivery.value = 'Bill via email'
        this.contactDetails.fields.email.props.required = true
        this.contactDetails.fields.email.props.rules.push(
          val => !!val || 'Email required!',
          val => /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w+)+$/.test(val) || 'Invalid email!',
          // val => /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(val) || 'Invalid email!'
        )
      } else {
        this.submission.fields.billDelivery.value = 'Deliver printed bill'
        this.contactDetails.fields.email.props.required = false
        this.contactDetails.fields.email.props.rules.splice(0, 20)
        this.contactDetails.fields.email.validate && this.$nextTick(() => this.contactDetails.fields.email.validate())
      }
    },
    'profile.fields.gender.value': function (val) {
      this.submission.fields.gender.value = val
      if (val === 'Male' || this.profile.fields.race.value === 'Malay') {
        this.profile.fields.maidenName.hide = true
        this.profile.fields.maidenName.value = null
      } else {
        this.profile.fields.maidenName.hide = false
      }
    },
    'profile.fields.race.value': function (val) {
      this.submission.fields.race.value = val
      if (val === 'Malay' || this.profile.fields.gender.value === 'Male') {
        this.profile.fields.maidenName.hide = true
        this.profile.fields.maidenName.value = null
      } else {
        this.profile.fields.maidenName.hide = false
      }
    },
    'profile.fields.id.value': function (val) {
      this.submission.fields.id.value = val
      if (!val || val.length < 12) return

      const now = DateTime.local().setZone('Asia/Kuala_Lumpur')
      const myKad = {
        dob: val.substr(0, 6),
        state: val.substr(6, 2),
        gender: val.substr(11, 1) % 2 ? 'Male' : 'Female',
      }
      const yy = Number(myKad.dob.substr(0, 2))
      const mm = myKad.dob.substr(2, 2)
      const dd = myKad.dob.substr(4, 2)
      let year = 1900 + yy

      this.submission.fields.id.value = myKad.dob + '-' + myKad.state + '-' + val.substr(8)
      if (now.year - year > 100) year = 2000 + yy
      myKad.dob = String(year) + '-' + mm + '-' + dd

      this.profile.fields.dob.validate && this.profile.fields.dob.validate()
      this.profile.fields.dob.value = myKad.dob

      this.profile.fields.gender.value = myKad.gender

      const state = getStateFromMyKad(val)
      if (state) {
        if (state === 'ArmedForcedID') {
          // this.profile.fields.idType.value = state
        } else {
          // this.profile.fields.idType.value = 'NewNRIC'
          // this.contactDetails.fields.state.value = state
        }
      } // else this.profile.fields.idType.value = 'NewNRIC'
    },
    'contactDetails.fields.billingAddress.value': function (val) {
      this.submission.fields.billingAddress.value = val
        ? formatAddress(this.contactDetails.fields.billingAddress.fields)
        : this.address
    },
    'contactDetails.fields.foreignerAddress.value': function (val) {
      this.submission.fields.foreignerAddress.value = val
        ? formatAddress(this.contactDetails.fields.foreignerAddress.fields)
        : this.foreignerAddress
    },
    'profile.fields.fcountry.value': function (val) {
      this.submission.fields.fcountry.value = val
    },
    'contactDetails.fields.street1.value': function () {
      this.submission.fields.address.value = this.address
    },
    'contactDetails.fields.street2.value': function () {
      this.submission.fields.address.value = this.address
    },
    'contactDetails.fields.city.value': function () {
      this.submission.fields.address.value = this.address
    },
    'contactDetails.fields.postalCode.value': function () {
      this.submission.fields.address.value = this.address
    },
    'contactDetails.fields.state.value': function () {
      this.submission.fields.address.value = this.address
    },
    foreignerStatus: function (val) {
      if (!val) return
      this.submission.fields.fstatus.value = val.text
    },
    'contactDetails.fields.fstreet1.value': function () {
      this.submission.fields.foreignerAddress.value = this.foreignerAddress
    },
    'contactDetails.fields.fstreet2.value': function () {
      this.submission.fields.foreignerAddress.value = this.foreignerAddress
    },
    'contactDetails.fields.fcity.value': function () {
      this.submission.fields.foreignerAddress.value = this.foreignerAddress
    },
    'contactDetails.fields.fpostalCode.value': function () {
      this.submission.fields.foreignerAddress.value = this.foreignerAddress
    },
    'contactDetails.fields.fstate.value': function () {
      this.submission.fields.foreignerAddress.value = this.foreignerAddress
    },
    'contactDetails.fields.billingAddress.fields.street1.value': function () {
      this.submission.fields.billingAddress.value = this.billToAddress
    },
    'contactDetails.fields.billingAddress.fields.street2.value': function () {
      this.submission.fields.billingAddress.value = this.billToAddress
    },
    'contactDetails.fields.billingAddress.fields.city.value': function () {
      this.submission.fields.billingAddress.value = this.billToAddress
    },
    'contactDetails.fields.billingAddress.fields.postalCode.value': function () {
      this.submission.fields.billingAddress.value = this.billToAddress
    },
    'contactDetails.fields.billingAddress.fields.state.value': function () {
      this.submission.fields.billingAddress.value = this.billToAddress
    },
    'registrationDetails.fields.msisdnList.props.items': {
      handler: function () {
        const requiredEcash = this.requiredEcash
        this.registrationDetails.fields.requiredEcash.value = getRm(requiredEcash)
        if (requiredEcash > getNumeralFromRm(this.erecharge.value)) {
          this.registrationDetails.fields.requiredEcash.error = 'Insufficient balance!'
        } else {
          this.registrationDetails.fields.requiredEcash.error = null
        }

        this.registrationDetails.fields.requiredEcash.validate && this.registrationDetails.fields.requiredEcash.validate(true)
        this.submission.fields.chargeable.value = getRm(this.chargeableAmount)
      },
      deep: true,
    },
  },
  async mounted () {
    this.submission.fields.msisdnSummary.props.items = this.registrationDetails.fields.msisdnList.props.items
    this.contactDetails.fields.billingAddress.fields = this.billingAddress
    const params = createGetParams({
      erecharge: true,
    })
    try {
      const response = await this.$rest.get('getDealerResources.php', params)

      this.erecharge.error = null
      this.erecharge.value = getRm(response.data.erecharge)
      if (response.data.b10Promo) {
        this.alertText = response.data.b10Promo.notification
        this.notification = true
      }
    } catch (error) {
      this.erecharge.error = error.message
    }

    if (this.id) {
      this.title = 'Re-submit Mnp'
      this.breadcrumbs[1].text = 'Re-submit Mnp'
      this.registrationDetails.fields.msisdnList.props.mnpResubmit = true
      this.submission.fields.chargeable.hide = true
      const params = createGetParams({
        portId: this.id,
      })

      this.$rest.get('getMnpPayload.php', params).then(function (response) {
        const data = response.data

        this.registrationDetails.fields.mnpStatusInfo.value = data.mnpStatus
        this.registrationDetails.fields.mnpStatusInfo.info = data.mnpStatusInfo
        this.registrationDetails.fields.ugu.value = data.ugu
        this.profile.fields.title.value = data.title
        this.profile.fields.name.value = data.name
        this.profile.fields.id.value = data.id
        // this.profile.fields.idType.value = data.idType
        this.profile.fields.dob.value = data.dob
        this.profile.fields.gender.value = data.value
        this.profile.fields.race.value = data.race
        this.profile.fields.maidenName.value = data.maiden
        data.mnp.forEach(item => {
          const newItem = Object.assign({
            detail: null,
            donor: null,
            iccid: null,
            icon: null,
            iconClass: null,
            msisdn: null,
            plan: {},
            resubmit: true,
          }, item)
          newItem.detail = item.msisdn + ', ' + item.plan.name +
            ', ' + item.donor
          newItem.icon = mnpIcon
          newItem.iconClass = mnpClass
          this.registrationDetails.fields.msisdnList.props.items.push(newItem)
        })
        this.contactDetails.fields.contactNumber.value = data.contact
        this.contactDetails.fields.billDelivery.value = data.billDelivery
        this.contactDetails.fields.email.value = data.email
        this.contactDetails.fields.street1.value = data.street1
        this.contactDetails.fields.street2.value = data.street2
        this.contactDetails.fields.city.value = data.city
        this.contactDetails.fields.postalCode.value = data.postalCode
        this.contactDetails.fields.state.value = data.state
        this.foreignerInfo = data.foreignerInfo
        data.foreignerInfo.forEach(item => {
          // this.profile.fields.fstatus.value = item.status
          if (item.status === 'T') {
            this.profile.fields.fstatus.value = { text: 'Tourist', value: 'T' }
          } else if (item.status === 'W') {
            this.profile.fields.fstatus.value = { text: 'Worker', value: 'W' }
          } else if (item.status === 'S') {
            this.profile.fields.fstatus.value = { text: 'Student', value: 'S' }
          }
          this.profile.fields.fcountry.value = item.issueCountry
          this.contactDetails.fields.fstreet1.value = item.regAddress
          this.contactDetails.fields.fstreet2.value = item.regAddressL2
          this.contactDetails.fields.fcity.value = item.regCity
          this.contactDetails.fields.fpostalCode.value = item.regPostcode
          this.contactDetails.fields.fstate.value = item.regState
          this.regName = item.regName
          this.regRef = item.regRef
          this.subid = item.subid
        })
        if (this.foreignerInfo === '') {
          this.profile.fields.fstatus.hide = true
          this.profile.fields.fcountry.hide = true
          this.submission.fields.fstatus.hide = true
          this.submission.fields.fcountry.hide = true
          this.submission.fields.foreignerAddress.hide = true
        } else {
          this.profile.fields.fstatus.hide = false
          this.profile.fields.fcountry.hide = false
          this.submission.fields.fstatus.hide = false
          this.submission.fields.fcountry.hide = false
          this.submission.fields.foreignerAddress.hide = false
        }
        this.contactDetails.fields.billingAddress.fields = data.billingAddress
          ? Object.assign(this.contactDetails.fields.billingAddress.fields, {
            street1: data.billingAddress.street1,
            street2: data.billingAddress.street2,
            city: data.billingAddress.city,
            postalCode: data.billingAddress.postalCode,
            state: data.billingAddress.state,
          }) : null
      }.bind(this)).catch(() => {
      })
    }
  },
  methods: {
    agreePdpa: function (val) {
      this.pdpaDialog = false
      if (!val) {
        this.status = this.submission.status = 'error'
        this.submission.errorMessage = 'You must ask the customer to agree to the Personal Data Protection Act before submitting the registration.'
        this.submission.submit = false
      }
      this.agree = val
    },
    // Override
    submit: function () {
      this.pdpaDialog = true
    },
    async doSubmit () {
      const payload = {
        portReqFormId: this.id,
        ugu: this.registrationDetails.fields.ugu.value,
        title: this.profile.fields.title.value,
        name: this.profile.fields.name.value,
        id: this.profile.fields.id.value,
        idType: 'NewNRIC', // this.profile.fields.idType.value,
        dob: this.profile.fields.dob.value,
        gender: this.profile.fields.gender.value,
        race: this.profile.fields.race.value,
        maiden: this.profile.fields.maidenName.value,
        contact: this.contactDetails.fields.contactNumber.value,
        billDelivery: this.contactDetails.fields.billDelivery.value,
        email: this.contactDetails.fields.email.value,
        street1: this.contactDetails.fields.street1.value,
        street2: this.contactDetails.fields.street2.value,
        city: this.contactDetails.fields.city.value,
        postalCode: this.contactDetails.fields.postalCode.value,
        state: this.contactDetails.fields.state.value,
        billingAddress: this.contactDetails.fields.billingAddress.value
          ? Object.assign({}, {
            street1: this.contactDetails.fields.billingAddress.fields.street1.value,
            street2: this.contactDetails.fields.billingAddress.fields.street2.value,
            city: this.contactDetails.fields.billingAddress.fields.city.value,
            postalCode: this.contactDetails.fields.billingAddress.fields.postalCode.value,
            state: this.contactDetails.fields.billingAddress.fields.state.value,
          }) : null,
        sp: [],
        newNumber: [],
        mnp: [],
        foreignerInfo: [],
      }

      /*
      if (this.contactDetails.fields.billingAddress.value) {
        payload.billingAddress = Object.assign({}, {
          street1: this.contactDetails.fields.billingAddress.fields.street1.value,
          street2: this.contactDetails.fields.billingAddress.fields.street2.value,
          city: this.contactDetails.fields.billingAddress.fields.city.value,
          postalCode: this.contactDetails.fields.billingAddress.fields.postalCode.value,
          state: this.contactDetails.fields.billingAddress.fields.state.value
        })
        // Fix bug where street2 is not submitted as a property
        // if (!payload.billingAddress.street2) payload.billingAddress.street2 = ''
      }
      */

      if (this.foreignerInfo) {
        payload.foreignerInfo.push({
          issueCountry: this.profile.fields.fcountry.value,
          regAddress: this.contactDetails.fields.street1.value,
          regAddressL2: this.contactDetails.fields.street2.value,
          regCity: this.contactDetails.fields.city.value,
          regPostcode: this.contactDetails.fields.postalCode.value,
          regState: this.contactDetails.fields.state.value,
          regName: this.regName,
          regRef: this.regRef,
          status: this.profile.fields.fstatus.value.value,
          subid: this.subid,
        })
      }

      this.registrationDetails.fields.msisdnList.props.items.forEach(item => {
        if (item.icon === spIcon) {
          payload.sp.push({
            msisdn: item.msisdn,
            iccid: item.iccid,
            plan: item.plan.code,
          })
        } else if (item.icon === newIcon) {
          payload.newNumber.push({
            msisdn: item.msisdn,
            iccid: item.iccid,
            plan: item.plan.code,
          })
        } else if (item.icon === mnpIcon) {
          payload.mnp.push({
            msisdn: item.msisdn,
            iccid: item.iccid,
            plan: item.plan.code,
            donor: item.donor,
          })
        }
      })

      const params = createPostParams({
        action: 'register',
        payload: payload,
      })

      try {
        const response = await this.$rest.post('postAction.php', params)

        this.$nextTick(() => {
          this.alertText = 'Your request to register Black numbers was successfully submitted.'
          this.status = this.submission.status = 'success'
          this.submission.submit = false
          if (this.isImpersonated()) {
            this.showSubmissionResponse(response.data)
          } else {
            this.downloadReceipt()
          }
        })
      } catch (e) {
        this.$nextTick(() => {
          this.status = this.submission.status = 'error'
          this.submission.errorMessage = e.message + ': ' + e.response.data
          this.submission.submit = false
          this.agree = false
        })
      }
    },
    async downloadReceipt () {
      let invoice = 'R-' + stringGen(9)
      invoice = invoice.toUpperCase()

      const payload = {
        invoice: invoice,
        name: this.profile.fields.name.value,
        street1: this.contactDetails.fields.street1.value,
        street2: this.contactDetails.fields.street2.value,
        city: this.contactDetails.fields.city.value,
        postalCode: this.contactDetails.fields.postalCode.value,
        state: this.contactDetails.fields.state.value,
        items: [],
      }

      this.registrationDetails.fields.msisdnList.props.items.forEach(item => {
        payload.items.push({
          msisdn: item.msisdn,
          plan: item.plan.code,
        })
      })

      const data = process.env.NODE_ENV === 'production' ? { payload: payload }
        : { impersonate: 223992, payload: payload }
      /* eslint-disable no-empty */
      try {
        const response = await this.$rest({
          url: process.env.NODE_ENV === 'production' ? location.protocol + '//' + location.hostname + '/api/pdf/registrationReceipt.php'
            : 'http://onesys.onexox.my/pdf/registrationReceipt.php',
          method: 'POST',
          responseType: 'blob',
          data: data,
        })

        const blob = new Blob([response.data], { type: 'application/pdf' })
        const link = document.createElement('a')

        link.href = window.URL.createObjectURL(blob)
        link.setAttribute('download', 'black_registration_receipt.pdf')
        document.body.appendChild(link)
        link.click()
      } catch (e) {}
    },
  },
}
